import React from 'react';
import pt from 'prop-types';
import {FieldWrap} from './MarketoForm.styles';
import {Field} from 'react-final-form';
import MarketoSelect from './MarketoSelect';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {useLocalization} from '@epic-core/localization';

//component options: input, select, textarea, select-multi, select-boolean
//type options: text, checkbox

const getErrorMsg = (fieldErrorMsg, metaData, getMessage) => {
    let errorMsg;
    if (fieldErrorMsg) {
        errorMsg = fieldErrorMsg;
    } else {
        const errorArray = metaData.error || [];

        if (typeof errorArray === 'string') {
            errorMsg = errorArray;
        } else {
            const [messageCode, ...args] = errorArray;
            if (messageCode) {
                errorMsg = getMessage(messageCode, args);
            }
        }
    }
    return errorMsg;
};

const MarketoField = ({field = {}, mutators = {}, active, values = {}}) => {
    const {id, type = 'text', component, label, parse, options, onChange, onSelectChange, validationArray = [], fieldErrorMsg, initHidden, readonly} = field;
    const {setValue, setFieldData} = mutators;
    const getMessage = useLocalization();
    return (
        <FieldWrap key={id} className={`${type} ${component} field-wrap ${readonly ? 'readonly' : ''}`}>
            <Field
                name={id}
                parse={parse}
                type={type}
                render={({input, meta, ...rest}) => {
                    const metaData = meta.data || {};
                    const errorMsg = getErrorMsg(fieldErrorMsg, metaData, getMessage);

                    if (metaData.hidden) return null; //toggled hidden
                    if (typeof metaData.hidden === 'undefined' && initHidden) return null; //initialize hidden

                    if (onChange && typeof onChange === 'function') {
                        input.nativeOnChange = input.nativeOnChange || input.onChange;
                        input.onChange = (e) => {
                            input.nativeOnChange(e);
                            const target = e.target;

                            let inputClone = input;
                            if (typeof input.checked !== 'undefined') {
                                //ensure the input.value matches the current value
                                inputClone = Object.assign({}, input, {value: input.checked});
                            }

                            onChange(e, {id, setValue, input: inputClone, meta, setFieldData, target});
                        };
                    }

                    const isInput = (component === 'input');
                    const isTextArea = (component === 'textarea');
                    const isMulti = (component === 'select-multi');
                    const isBooleanSelect = (component === 'select-boolean');
                    const required = (validationArray && validationArray.indexOf('required') !== -1) || metaData.required;
                    const hasError = (meta.touched || meta.data.touched) && !meta.data.valid;

                    let inputEl;
                    if (component === 'select' || isMulti || isBooleanSelect) {
                        inputEl = (
                            <MarketoSelect
                                key={`select-${id}`}
                                id={id}
                                mutators={mutators}
                                options={options}
                                onSelectChange={onSelectChange}
                                isMulti={isMulti}
                                isBoolean={isBooleanSelect}
                                label={label}
                                value={input.value}
                                required={required}
                                helperText={hasError ? errorMsg : ''}
                                error={hasError} />
                        );
                    } else {
                        const {value, ...inputRest} = input;
                        delete inputRest.nativeOnChange;
                        if (isInput && type === 'checkbox') {
                            inputEl = (
                                <FormControlLabel
                                    label={label}
                                    required={required}
                                    error={errorMsg}
                                    control={
                                        <Checkbox
                                            key={`input-chkbox-${id}`}
                                            {...inputRest}
                                            value={value}
                                        />
                                    } />
                            )
                        } else if (type === 'text') {
                            inputEl = (
                                <TextField
                                    key={`input-${id}`}
                                    {...inputRest}
                                    value={value}
                                    label={label}
                                    required={required}
                                    fullWidth
                                    helperText={hasError ? errorMsg : ''}
                                    error={hasError}
                                    disabled={readonly}
                                    multiline={isTextArea} 
                                    inputProps={isTextArea && {
                                        spellCheck: 'false',
                                        'data-gramm': 'false',
                                        'data-gramm-editor': 'false',
                                        'data-enable-grammarly': 'false'
                                    }} />
                            )
                        }
                    }
                    return inputEl;
                }}
            />
        </FieldWrap>
    );
}

MarketoField.propTypes = {
    field: pt.object,
    mutators: pt.object,
    active: pt.string,
    values: pt.object
};

export default MarketoField;
